/**
 * `CalendarChromeBus` — the body↔legend shared-state bus.
 *
 * The body plugin (`TimelineColumnLayoutPlugin`, currently also
 * `CalendarSheetPlugin`) produces gesture-driven state — pointer
 * cursor position, active gesture count — and each `TimelineColumnLegend`
 * consumes that state to update its cursor marker and pointer
 * enter/leave display.
 *
 * Before the bus, the legend reached for `plugin.columnSignals.*` and
 * `plugin.$attachedLegends` directly, which forced every plugin role
 * that hosted a calendar legend (renderer, layout, alias) to expose
 * those fields. The alias plugin in particular had to mirror them via
 * read-through getters, dragging the body-renderer reference into the
 * chrome plugin's constructor.
 *
 * The bus is constructed once per body-plugin instance (so per-app, not
 * per-row) and passed to whoever needs it via DI:
 *
 *   - The body plugin holds the bus and writes to its `currentCursor`
 *     and `currentGestureCount` signals from gesture-manager callbacks.
 *   - The alias plugin receives the same bus reference and forwards it
 *     to `TimelineColumnLegend.factory(deps, icon)` so the legend's per-bind closure
 *     captures the bus.
 *   - Each legend self-registers via `bus.attach(this)` /
 *     `bus.detach(this)` in its connected/disconnected callbacks.
 *   - The body plugin iterates `bus.iterateLegends()` from
 *     `queuePointerEnterLeave` to broadcast hover state.
 */

import { LiveData } from "@tento-core/livedata";
import { $$ } from "@tento-ui/fastdom";
import type { Option, RowLocalId } from "@tento-lona/sheets";

/** Where a calendar's time cursor stands: the row it is over and its offset into the day, in milliseconds. */
export type CalendarCursorState = {
  readonly rid: RowLocalId;
  readonly offsetMs: number;
};

/** Read-only surface — what the legend (and other consumers) see. */
export interface CalendarChromeBus {
  readonly currentCursor: LiveData.Readable<Option<CalendarCursorState>>;
  readonly currentGestureCount: LiveData.Readable<number>;
  attach(legend: HasToggleCurrentCursorDisplay): void;
  detach(legend: HasToggleCurrentCursorDisplay): void;
  iterateLegends(): IterableIterator<HasToggleCurrentCursorDisplay>;
  /**
   * Broadcast the current hover state to every registered legend.
   * Reads the body-supplied `pointerEnteredRowIds()` to decide which
   * legends should show their cursor marker. The body invokes this
   * from its gesture-manager pointer callbacks; a freshly-attached
   * legend invokes it from `connectedCallback` to pick up the
   * existing hover state without waiting for the next pointer event.
   */
  queuePointerEnterLeave(): void;
}

/** Structural type — what the body needs from a registered legend. */
export interface HasToggleCurrentCursorDisplay {
  /** Whether a pointer over a column reported against `rowId` is this legend's to show. */
  showsCursorOf(rowId: RowLocalId): boolean;
  toggleCurrentCursorDisplay(shown: boolean): void;
}

/** Body-side surface — the body plugin writes through these. */
export interface CalendarChromeBusOwner extends CalendarChromeBus {
  readonly currentCursor: LiveData<Option<CalendarCursorState>>;
}

function makeCalendarChromeBus(
  currentGestureCount: LiveData.Readable<number>,
  /**
   * Body-supplied accessor for "which row ids are currently under the
   * pointer". Today the body wraps `gestureManager.pointerEnteredInfo`;
   * the bus stays agnostic to the gesture system shape so it can host
   * non-gesture chrome producers (debug overlays, scripted hover) in
   * future.
   */
  pointerEnteredRowIds: () => Iterable<RowLocalId>,
): CalendarChromeBusOwner {
  const currentCursor = new LiveData<Option<CalendarCursorState>>(null);
  const legends = new Set<HasToggleCurrentCursorDisplay>();
  let queued = false;
  return {
    currentCursor,
    currentGestureCount,
    attach(legend) {
      legends.add(legend);
    },
    detach(legend) {
      legends.delete(legend);
    },
    iterateLegends() {
      return legends.values();
    },
    queuePointerEnterLeave() {
      if (queued) return;
      queued = true;
      $$.mutate(() => {
        queued = false;
        const hovered = [...pointerEnteredRowIds()];
        for (const legend of legends) {
          legend.toggleCurrentCursorDisplay(hovered.some((id) => legend.showsCursorOf(id)));
        }
      });
    },
  };
}

/** Values that go with `CalendarChromeBus`: `make`. */
export namespace CalendarChromeBus {
  export const make = makeCalendarChromeBus;
}
